import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, CalendarIcon, Save, RefreshCw } from "lucide-react";
import { format } from "date-fns";

const SUPPORTED_CURRENCIES = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" }
];

const CreateInvoice = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState("");
  const [subscriptionPlan, setSubscriptionPlan] = useState("free");
  
  // Form state
  const [formData, setFormData] = useState({
    invoice_number: "",
    business_name: "",
    business_address: "",
    client_id: "",
    client_name: "",
    client_email: "",
    client_address: "",
    invoice_date: new Date(),
    due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // 14 days from now
    items: [{ description: "", quantity: 1, unit_price: 0 }],
    tax_percentage: 0,
    currency: "USD",
    notes: "",
    payment_instructions: "",
    is_recurring: false,
    recurring_schedule: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsRes, invoiceNumRes, userRes] = await Promise.all([
          axios.get(`${API}/clients`, { withCredentials: true }),
          axios.get(`${API}/next-invoice-number`, { withCredentials: true }),
          axios.get(`${API}/auth/me`, { withCredentials: true })
        ]);
        setClients(clientsRes.data);
        setNextInvoiceNumber(invoiceNumRes.data.next_invoice_number);
        setSubscriptionPlan(userRes.data.subscription_plan || "free");
        setFormData(prev => ({
          ...prev,
          invoice_number: invoiceNumRes.data.next_invoice_number
        }));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoadingClients(false);
      }
    };
    fetchData();
  }, []);

  const handleClientSelect = (clientId) => {
    if (clientId === "new") {
      setFormData({
        ...formData,
        client_id: "",
        client_name: "",
        client_email: "",
        client_address: ""
      });
    } else {
      const client = clients.find(c => c.client_id === clientId);
      if (client) {
        setFormData({
          ...formData,
          client_id: client.client_id,
          client_name: client.client_name,
          client_email: client.client_email,
          client_address: client.client_address
        });
      }
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: 1, unit_price: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => {
    return sum + (Number(item.quantity) * Number(item.unit_price));
  }, 0);
  const taxAmount = subtotal * (Number(formData.tax_percentage) / 100);
  const total = subtotal + taxAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.client_name || !formData.client_email) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error("Please fill in all item details");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        invoice_number: formData.invoice_number || null, // Let backend auto-generate if empty
        invoice_date: format(formData.invoice_date, "yyyy-MM-dd"),
        due_date: format(formData.due_date, "yyyy-MM-dd"),
        items: formData.items.map(item => ({
          description: item.description,
          quantity: Number(item.quantity),
          unit_price: Number(item.unit_price)
        })),
        tax_percentage: Number(formData.tax_percentage)
      };

      const res = await axios.post(`${API}/invoices`, payload, { withCredentials: true });
      toast.success("Invoice created successfully!");
      navigate(`/invoices/${res.data.invoice_id}/edit`);
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="create-invoice-page">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Create Invoice
          </h1>
          <p className="text-muted-foreground">Fill in the details to generate your invoice</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Form Section */}
            <div className="lg:col-span-3 space-y-6">
              {/* Business Info */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-base">Your Business Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      data-testid="business-name-input"
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      placeholder="Your Company Name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="business_address">Business Address</Label>
                    <Textarea
                      id="business_address"
                      data-testid="business-address-input"
                      value={formData.business_address}
                      onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                      placeholder="123 Business Street&#10;City, State 12345"
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Client Info */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-base">Client Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingClients ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <div>
                      <Label>Select Client</Label>
                      <Select onValueChange={handleClientSelect} data-testid="client-select">
                        <SelectTrigger data-testid="client-select-trigger">
                          <SelectValue placeholder="Select a client or add new" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">+ Add New Client</SelectItem>
                          {clients.map((client) => (
                            <SelectItem key={client.client_id} value={client.client_id}>
                              {client.client_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label htmlFor="client_name">Client Name *</Label>
                    <Input
                      id="client_name"
                      data-testid="client-name-input"
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      placeholder="Client Name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="client_email">Client Email *</Label>
                    <Input
                      id="client_email"
                      type="email"
                      data-testid="client-email-input"
                      value={formData.client_email}
                      onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                      placeholder="client@email.com"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="client_address">Client Address</Label>
                    <Textarea
                      id="client_address"
                      data-testid="client-address-input"
                      value={formData.client_address}
                      onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                      placeholder="Client Address"
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Invoice Details */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-base">Invoice Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="invoice_number">Invoice Number</Label>
                      <Input
                        id="invoice_number"
                        data-testid="invoice-number-input"
                        value={formData.invoice_number}
                        onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                        placeholder={nextInvoiceNumber || "Auto-generated"}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Leave blank for auto</p>
                    </div>
                    <div>
                      <Label>Currency</Label>
                      <Select 
                        value={formData.currency} 
                        onValueChange={(value) => setFormData({ ...formData, currency: value })}
                        data-testid="currency-select"
                      >
                        <SelectTrigger data-testid="currency-select-trigger">
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent>
                          {SUPPORTED_CURRENCIES.map((cur) => (
                            <SelectItem key={cur.code} value={cur.code}>
                              {cur.symbol} {cur.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Invoice Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                            data-testid="invoice-date-btn"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(formData.invoice_date, "MMM dd, yyyy")}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={formData.invoice_date}
                            onSelect={(date) => date && setFormData({ ...formData, invoice_date: date })}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label>Due Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                            data-testid="due-date-btn"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(formData.due_date, "MMM dd, yyyy")}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={formData.due_date}
                            onSelect={(date) => date && setFormData({ ...formData, due_date: date })}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Recurring Invoice Option - Pro Feature */}
                  <div className="border-t pt-4 mt-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="text-base flex items-center gap-2">
                          <RefreshCw className="w-4 h-4" />
                          Recurring Invoice
                          {subscriptionPlan !== "pro" && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Pro</span>
                          )}
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Automatically generate this invoice on a schedule
                        </p>
                      </div>
                      <Switch
                        checked={formData.is_recurring}
                        onCheckedChange={(checked) => {
                          if (subscriptionPlan !== "pro" && checked) {
                            toast.info("Recurring invoices are a Pro feature. Upgrade to enable.");
                            return;
                          }
                          setFormData({ ...formData, is_recurring: checked });
                        }}
                        data-testid="recurring-switch"
                      />
                    </div>
                    
                    {formData.is_recurring && (
                      <div className="mt-4">
                        <Label>Recurring Schedule</Label>
                        <Select 
                          value={formData.recurring_schedule} 
                          onValueChange={(value) => setFormData({ ...formData, recurring_schedule: value })}
                          data-testid="recurring-schedule-select"
                        >
                          <SelectTrigger data-testid="recurring-schedule-trigger">
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Invoice Items */}
              <Card className="bg-white">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Invoice Items</CardTitle>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Item
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground uppercase">
                      <div className="col-span-6">Description</div>
                      <div className="col-span-2">Qty</div>
                      <div className="col-span-3">Rate</div>
                      <div className="col-span-1"></div>
                    </div>
                    
                    {formData.items.map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-center" data-testid={`invoice-item-${index}`}>
                        <div className="col-span-6">
                          <Input
                            placeholder="Item description"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            data-testid={`item-description-${index}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            data-testid={`item-quantity-${index}`}
                          />
                        </div>
                        <div className="col-span-3">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              className="pl-7"
                              value={item.unit_price}
                              onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                              data-testid={`item-price-${index}`}
                            />
                          </div>
                        </div>
                        <div className="col-span-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(index)}
                            disabled={formData.items.length === 1}
                            data-testid={`remove-item-${index}`}
                          >
                            <Trash2 className="w-4 h-4 text-muted-foreground" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Tax */}
                  <div className="mt-6 pt-4 border-t border-border">
                    <div className="flex items-center justify-between max-w-xs ml-auto">
                      <Label htmlFor="tax">Tax (%)</Label>
                      <Input
                        id="tax"
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        className="w-24 text-right"
                        value={formData.tax_percentage}
                        onChange={(e) => setFormData({ ...formData, tax_percentage: e.target.value })}
                        data-testid="tax-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-base">Additional Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      data-testid="notes-input"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Thank you for your business!"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="payment_instructions">Payment Instructions</Label>
                    <Textarea
                      id="payment_instructions"
                      data-testid="payment-instructions-input"
                      value={formData.payment_instructions}
                      onChange={(e) => setFormData({ ...formData, payment_instructions: e.target.value })}
                      placeholder="Bank transfer details, PayPal, etc."
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Preview Section */}
            <div className="lg:col-span-2">
              <div className="sticky top-24">
                <Card className="bg-white invoice-paper">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-muted-foreground uppercase tracking-wider">Invoice Preview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4 text-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-xl font-bold text-primary">INVOICE</p>
                          <p className="text-xs text-muted-foreground">#{formData.invoice_number || nextInvoiceNumber || "Auto"}</p>
                        </div>
                        <div className="text-right text-xs text-muted-foreground">
                          <p>{format(formData.invoice_date, "MMM dd, yyyy")}</p>
                          <p>Due: {format(formData.due_date, "MMM dd, yyyy")}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <p className="font-semibold text-muted-foreground mb-1">FROM</p>
                          <p className="font-medium">{formData.business_name || "Your Business"}</p>
                          <p className="text-muted-foreground whitespace-pre-line">{formData.business_address}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-muted-foreground mb-1">TO</p>
                          <p className="font-medium">{formData.client_name || "Client Name"}</p>
                          <p className="text-muted-foreground">{formData.client_email}</p>
                        </div>
                      </div>

                      <div className="border-t border-b border-border py-3">
                        {formData.items.map((item, i) => (
                          <div key={i} className="flex justify-between py-1">
                            <span className="truncate flex-1 mr-2">{item.description || "Item"}</span>
                            <span className="font-mono text-right">
                              ${(Number(item.quantity) * Number(item.unit_price)).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="space-y-1 text-right">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Subtotal</span>
                          <span className="font-mono">${subtotal.toFixed(2)}</span>
                        </div>
                        {Number(formData.tax_percentage) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Tax ({formData.tax_percentage}%)</span>
                            <span className="font-mono">${taxAmount.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-lg font-bold text-primary pt-2 border-t border-border">
                          <span>Total</span>
                          <span className="font-mono">${total.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="mt-4 space-y-3">
                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90 btn-hover-lift"
                    disabled={loading}
                    data-testid="create-invoice-submit-btn"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Create Invoice
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default CreateInvoice;
